import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, ArrowLeft, CheckCircle, Lock, AlertCircle,
  Building2, User, FileText, Shield
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface PaymentConfig {
  merchantId: string;
  apiKey: string;
  sandbox: boolean;
  autoWithdrawal: boolean;
  withdrawalAccount?: string;
}

export default function OpenpayConfig() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [config, setConfig] = useState<PaymentConfig>({
    merchantId: '',
    apiKey: '',
    sandbox: true,
    autoWithdrawal: true,
    withdrawalAccount: ''
  });
  const [isConnected, setIsConnected] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);

  const { isSuperAdmin } = useAuth();

  const handleConnect = () => {
    // Simulate connection
    setTimeout(() => {
      setIsConnected(true);
      alert('✅ Conectado exitosamente a Openpay');
    }, 1500);
  };

  const handleTestPayment = () => {
    setShowTestModal(true);
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#2D3748] dark:text-[var(--v-text-primary)] mb-2">Acceso Restringido</h2>
          <p className="text-gray-500 dark:text-slate-400">Solo el Super Administrador puede configurar los pagos.</p>
          <button 
            onClick={() => navigate('/home/dashboard')}
            className="mt-4 px-6 py-2 bg-[#36CCD8] text-white font-bold rounded-xl"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-white dark:bg-[var(--v-surface-1)] border-b border-gray-200 dark:border-[var(--v-surface-3)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="p-2 text-[#2D3748] dark:text-white hover:bg-gray-100 dark:hover:bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">Configuración Openpay</h1>
              <p className="text-xs text-gray-500 dark:text-slate-400">Integración de pagos</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-2xl mx-auto">
        {/* Status Card */}
        <div className={`rounded-2xl p-6 mb-6 ${isConnected ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isConnected ? 'bg-emerald-500' : 'bg-amber-500'}`}>
              {isConnected ? <CheckCircle className="w-7 h-7 text-white" /> : <AlertCircle className="w-7 h-7 text-white" />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#2D3748] dark:text-[var(--v-text-primary)] mb-1">
                {isConnected ? 'Conectado a Openpay' : 'No conectado'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                {isConnected 
                  ? 'Tu condominio puede recibir pagos electrónicos.' 
                  : 'Configura tu cuenta de Openpay para habilitar pagos con tarjeta.'}
              </p>
            </div>
          </div>
        </div>

        {/* Connection Form */}
        <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <h3 className="font-bold text-[#2D3748] dark:text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#36CCD8]" /> Credenciales Openpay
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-[#2D3748] dark:text-white mb-2">Merchant ID</label>
              <input 
                type="text" 
                value={config.merchantId}
                onChange={(e) => setConfig({...config, merchantId: e.target.value})}
                placeholder="mxxxxxxxxxxxxxxxx"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-[var(--v-surface-3)] rounded-xl"
              />
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Encuentra esto en tu dashboard de Openpay</p>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-[#2D3748] dark:text-white mb-2">API Key</label>
              <input 
                type="password" 
                value={config.apiKey}
                onChange={(e) => setConfig({...config, apiKey: e.target.value})}
                placeholder="sk_xxxxxxxxxxxx"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-[var(--v-surface-3)] rounded-xl"
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-950 rounded-xl">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                <div>
                  <p className="font-medium text-[#2D3748] dark:text-white">Modo Sandbox</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Usar para pruebas (sin cargos reales)</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={config.sandbox}
                  onChange={(e) => setConfig({...config, sandbox: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-[var(--v-surface-1)] after:border-gray-300 dark:border-slate-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#36CCD8]"></div>
              </label>
            </div>
          </div>

          {!isConnected ? (
            <button 
              onClick={handleConnect}
              className="w-full mt-6 py-4 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center justify-center gap-2"
            >
              <Lock className="w-5 h-5" /> Conectar con Openpay
            </button>
          ) : (
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setIsConnected(false)}
                className="flex-1 py-3 border border-red-200 text-red-600 font-bold rounded-xl hover:bg-red-50"
              >
                Desconectar
              </button>
              <button 
                onClick={handleTestPayment}
                className="flex-1 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4]"
              >
                Probar Pago
              </button>
            </div>
          )}
        </div>

        {/* Withdrawal Settings */}
        {isConnected && (
          <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="font-bold text-[#2D3748] dark:text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#36CCD8]" /> Configuración de Retiros
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-950 rounded-xl">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                  <div>
                    <p className="font-medium text-[#2D3748] dark:text-white">Retiro Automático</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Transferir fondos automáticamente</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={config.autoWithdrawal}
                    onChange={(e) => setConfig({...config, autoWithdrawal: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-[var(--v-surface-1)] after:border-gray-300 dark:border-slate-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#36CCD8]"></div>
                </label>
              </div>

              {config.autoWithdrawal && (
                <div>
                  <label className="block text-sm font-bold text-[#2D3748] dark:text-white mb-2">Cuenta CLABE para depósitos</label>
                  <input 
                    type="text" 
                    value={config.withdrawalAccount}
                    onChange={(e) => setConfig({...config, withdrawalAccount: e.target.value})}
                    placeholder="002xxxxxxxxxxxxxxxxx"
                    maxLength={18}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-[var(--v-surface-3)] rounded-xl"
                  />
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">18 dígitos</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium mb-1">¿Necesitas una cuenta de Openpay?</p>
              <p className="text-sm text-blue-600">
                Visita <a href="https://www.openpay.mx" target="_blank" className="underline">openpay.mx</a> para crear tu cuenta de comercio.
                Es necesario ser persona moral con acta constitutiva.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Test Payment Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#2D3748] dark:text-white mb-4">Pago de Prueba</h3>
            <p className="text-gray-600 dark:text-slate-300 mb-4">Se realizará un cargo de $10.00 MXN para verificar la conexión. Este cargo se reembolsará en 24-48 horas.</p>
            
            <div className="bg-gray-50 dark:bg-slate-950 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-500 dark:text-slate-400">Tarjeta de prueba:</p>
              <p className="font-mono text-[#2D3748] dark:text-white">4111111111111111</p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">Cualquier fecha futura y CVV</p>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => setShowTestModal(false)}
                className="flex-1 py-3 border border-gray-200 dark:border-[var(--v-surface-3)] rounded-xl"
              >
                Cancelar
              </button>
              <button 
                onClick={() => { setShowTestModal(false); alert('✅ Pago de prueba exitoso'); }}
                className="flex-1 py-3 bg-[#36CCD8] text-white rounded-xl"
              >
                Proceder con $10.00
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
